/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/** get some code from https://github.com/angular/material2 */

import { Directionality } from "@angular/cdk/bidi";
import { ChangeDetectorRef, Component, ContentChildren, EventEmitter, Input, Optional, Output, QueryList, TemplateRef, ViewChild } from "@angular/core";
import { Router } from "@angular/router";

import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { NzSafeAny } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";

import { NZ_TAB_SET, UiTabComponent } from "../../tab.component";
import { UiTabNavBarComponent } from "../tab-nav-bar/tab-nav-bar.component";
import { NzTabSetComponent } from "ng-zorro-antd/tabs";
import { UiAnimatedInterface, UiTabChangeEvent, UiTabPosition, UiTabsCanDeactivateFn, UiTabScrollEvent, UiTabType } from "../../models/tab.models";
import { UiSizeLDSType } from "../../../core/models/ui-size.models";

@Component({
  selector: "ui-tabset",
  exportAs: "uiTabset",
  providers: [
    {
      provide: NZ_TAB_SET,
      useExisting: UiTabSetComponent,
    },
  ],
  templateUrl: "./tabset.component.html",
})
export class UiTabSetComponent extends NzTabSetComponent {
  @Input("uiSelectedIndex")
  public get uiSelectedIndex(): number | null {
    return this.nzSelectedIndex;
  }
  public set uiSelectedIndex(value: number | null) {
    this.nzSelectedIndex = value;
  }
  @Input("uiTabPosition") public nzTabPosition: UiTabPosition = "top";
  @Input("uiTabBarExtraContent") public nzTabBarExtraContent?: TemplateRef<void>;
  @Input("uiCanDeactivate") public nzCanDeactivate: UiTabsCanDeactivateFn | null = null;
  @Input("uiAddIcon") public nzAddIcon: string | TemplateRef<NzSafeAny> = "outline:plus";
  @Input("uiTabBarStyle") public nzTabBarStyle: { [key: string]: string } | null = null;
  @Input("uiType") @WithConfig() public nzType: UiTabType = "line";
  @Input("uiSize") @WithConfig() public nzSize: UiSizeLDSType = "default";
  @Input("uiAnimated") @WithConfig() public nzAnimated: UiAnimatedInterface | boolean = true;
  @Input("uiTabBarGutter") @WithConfig() public nzTabBarGutter?: number = undefined;
  @Input("uiHideAdd") @InputBoolean() public nzHideAdd = false;
  @Input("uiCentered") @InputBoolean() public nzCentered = false;
  @Input("uiHideAll") @InputBoolean() public nzHideAll = false;
  @Input("uiLinkRouter") @InputBoolean() public nzLinkRouter = false;
  @Input("uiLinkExact") @InputBoolean() public nzLinkExact = true;

  @Output("uiSelectChange") public nzSelectChange: EventEmitter<UiTabChangeEvent> = new EventEmitter<UiTabChangeEvent>(true);
  @Output("uiSelectedIndexChange") public nzSelectedIndexChange: EventEmitter<number> = new EventEmitter<number>();
  @Output("uiTabListScroll") public nzTabListScroll = new EventEmitter<UiTabScrollEvent>();
  @Output("uiClose") public nzClose = new EventEmitter<{ index: number }>();
  @Output("uiAdd") public nzAdd = new EventEmitter<void>();

  // Pick up only direct descendants under ivy rendering engine
  // We filter out only the tabs that belong to this tab set in `tabs`.
  @ContentChildren(UiTabComponent, { descendants: true })
  public allTabs: QueryList<UiTabComponent> = new QueryList<UiTabComponent>();
  @ViewChild(UiTabNavBarComponent, { static: false }) public tabNavBarRef!: UiTabNavBarComponent;

  public constructor(public uiConfigService: NzConfigService, cdr: ChangeDetectorRef, @Optional() directionality: Directionality, @Optional() router: Router) {
    super(uiConfigService, cdr, directionality, router);
  }
}
